/**
 * La décision de la porte « Finalise ton compte » — sortie de `members.ts`
 * pour être testable.
 *
 * Elle vivait en ligne dans la query `members.myProfileGate`, où elle n'était
 * atteignable qu'en montant le runtime Convex : c'est-à-dire nulle part, et
 * aucun test ne la couvrait. Or c'est une expression booléenne à cinq entrées
 * dont deux exceptions (founder, superadmin) — exactement la forme qu'on casse
 * en la relisant vite. Ici elle est pure, donc elle a un banc.
 *
 * 🩸 CE QU'ELLE A CESSÉ DE FAIRE LE 19-08-2026 : bloquer sur l'adresse USDC.
 * La condition était `(!hasWallet || !hasDiscordVerified)`. Décision de Kayla :
 * « vérifier l'adresse USDC avant d'entrer, j'aimerais que ce soit pas
 * obligatoire, et s'il veut il pourra mettre son adresse crypto plus tard. Par
 * contre il est obligé de faire le truc Discord, ça lui fasse rejoindre le
 * serveur. » Le wallet reste posable ensuite par le membre lui-même
 * (`WalletTab`, self-service ; `members.setWalletAddress` autorise `isSelf`) —
 * personne ne se retrouve sans moyen d'être payé.
 *
 * Pourquoi Discord reste dur : son OAuth demande `guilds.members.read`, donc
 * il ne prouve pas seulement la possession du compte mais la PRÉSENCE sur le
 * serveur. C'est la porte par laquelle Kayla récupère les candidats, depuis
 * qu'ils sont invités dès le formulaire et non plus au bout du test.
 */

/** L'instant à partir duquel un membre est considéré comme « invité récent ».
 *  Les membres antérieurs ne sont jamais gatés rétroactivement. */
export const PROFILE_GATE_LAUNCH_MS = 1783857202178;

export interface EtatDuMembre {
  /** Superadmin plateforme — jamais gaté (god-mode). */
  isSuperadmin: boolean;
  /** Le patron n'est pas un invité. */
  isFounder: boolean;
  /** `joinedAt ?? createdAt` du membre, en ms. */
  joinedAtMs: number;
  /** Drapeau posé aux points d'acceptation d'invitation. `null` = absent. */
  profileCompletionRequiredAt: number | null;
  /** `discordVerifiedAt` non nul — compte connecté ET présent sur le serveur. */
  hasDiscordVerified: boolean;
  /**
   * Le SLUG du rôle du membership (`roles.slug`), ou `null` si irrésolu.
   *
   * 🩸 Ajouté le 27-08-2026. La porte était AVEUGLE AU RÔLE : tout membre
   * arrivé après le lancement y passait. Kayla a invité le collaborateur
   * externe de cumgram.com — un partenaire, pas un candidat — et il s'est vu
   * demander de rejoindre le Discord et de poser une adresse USDC, puis de
   * passer le test de chatteur. Ce n'est pas son travail.
   *
   * ⛔ `null` = on ne sait pas → on RETIENT (le comportement d'avant). Un rôle
   * irrésolu ne doit pas ouvrir une porte de recrutement : le pire d'un défaut
   * fermé est un employé qui appelle Kayla, le pire d'un défaut ouvert est un
   * candidat qui entre sans être passé par le Discord.
   */
  roleSlug: string | null;
}

/**
 * Les rôles recrutés PAR ce parcours — les seuls que la porte vise.
 *
 * Le parcours Discord + test existe pour l'entonnoir de candidature : prouver
 * la présence sur le serveur, puis passer le test. Un `operator`, un `manager`
 * ou un `ceo` n'y entre pas — il est invité directement par Kayla, et lui
 * demander de rejoindre le Discord des candidats n'a aucun sens.
 *
 * 📏 Mesuré le 27-08-2026 : les 140 invitations en attente portent TOUTES le
 * rôle `chatter`. Le jour où Kayla recrute des VA par le même entonnoir, `va`
 * s'ajoute ici — et nulle part ailleurs.
 */
export const ROLES_DU_RECRUTEMENT: readonly string[] = ["chatter"];

/**
 * `true` = l'écran bloquant « Finalise ton compte » doit s'afficher.
 *
 * ⚠️ L'adresse USDC n'entre volontairement PAS dans cette signature. Si
 * quelqu'un la rajoute un jour en paramètre, c'est qu'il est en train de
 * refermer la porte que Kayla a ouverte — qu'il relise l'en-tête d'abord.
 */
export function exigeFinalisationDuProfil(
  membre: EtatDuMembre,
  lancementMs: number = PROFILE_GATE_LAUNCH_MS,
): boolean {
  // God-mode : un superadmin n'est jamais retenu, quel que soit son état.
  if (membre.isSuperadmin) return false;
  // Le founder non plus — il n'est pas entré par une invitation.
  if (membre.isFounder) return false;

  if (!estSoumisAuParcours(membre, lancementMs)) return false;

  // Et la seule chose qui reste à faire, c'est Discord.
  return !membre.hasDiscordVerified;
}

/**
 * « Cette personne est-elle passée par le parcours de recrutement ? »
 *
 * Extrait de `exigeFinalisationDuProfil`, dont c'était la troisième garde, pour
 * que le gate du TEST s'appuie exactement sur la même — un membre exempté de
 * Discord doit l'être du test aussi, sinon on aurait deux définitions de
 * « nouveau » qui divergeraient au premier changement.
 *
 * Le gate ne vise que les invités « récents » (ou explicitement marqués) : il
 * ne se referme pas rétroactivement sur l'équipe déjà en place.
 */
export function estSoumisAuParcours(
  membre: Pick<
    EtatDuMembre,
    "isSuperadmin" | "isFounder" | "joinedAtMs" | "profileCompletionRequiredAt" | "roleSlug"
  >,
  lancementMs: number = PROFILE_GATE_LAUNCH_MS,
): boolean {
  if (membre.isSuperadmin) return false;
  if (membre.isFounder) return false;
  // Le parcours ne vise que les rôles recrutés par lui. `null` (rôle irrésolu)
  // reste soumis : défaut fermé, cf le commentaire de `roleSlug`.
  // `typeof === "string"` et pas `!== null` : `undefined` (champ absent d'un
  // appelant qui n'a pas été mis à jour) exemptait tout le monde en silence —
  // le défaut se serait ouvert au lieu de se fermer. Trouvé par un banc voisin
  // qui construisait son objet à la main.
  if (
    typeof membre.roleSlug === "string" &&
    !ROLES_DU_RECRUTEMENT.includes(membre.roleSlug)
  ) {
    return false;
  }
  return (
    membre.joinedAtMs >= lancementMs || membre.profileCompletionRequiredAt !== null
  );
}

/** Ce que le gate doit afficher. */
export type EcranDuGate = "discord" | "ouvert";

/**
 * Une seule porte : Discord.
 *
 * 🩸 La porte du TEST (19-08-2026, « tant qu'il n'a pas fait le test, il ne
 * peut pas accéder à son espace ») a été RETIRÉE le 06-10-2026. Kayla : « quand
 * les gens se connectent au cockpit, il n'y a pas besoin de test. Ils ont juste
 * besoin de se connecter au Discord, d'autoriser […]. S'ils veulent un shift,
 * ils sont obligés de créer un ticket. » Le test vit désormais derrière le
 * ticket : Francis envoie le lien, et sans test réussi il n'y a pas de shift
 * (`ticketsAgent.peutRecevoirUnShift`). Le cockpit, lui, s'ouvre.
 */
export function resoudreEcranDuGate(entree: { exigeDiscord: boolean }): EcranDuGate {
  return entree.exigeDiscord ? "discord" : "ouvert";
}

/**
 * L'instant où l'accueil (pseudo, photo, espace de travail) a été mis en
 * service. Un membre dont le Discord a été vérifié AVANT n'est jamais renvoyé
 * à l'accueil : il travaille déjà, et le lui imposer au prochain chargement
 * serait une porte rétroactive — exactement ce que `PROFILE_GATE_LAUNCH_MS`
 * évite pour Discord.
 */
export const ACCUEIL_LAUNCH_MS = 1791378000000; // 07-10-2026

/**
 * `true` = Discord est fait, mais le chatteur n'a pas encore CONFIRMÉ son
 * accueil (pseudo, photo, thème, fond, taille). Kayla, 07-10-2026 : « quand un
 * chatter a accès au cockpit pour la première fois […] ça lui demande aussi de
 * personnaliser le thème du cockpit, et qu'il y ait une preview ».
 *
 * Tant que Discord manque, c'est `exigeFinalisationDuProfil` qui parle : les
 * deux ne sont jamais vrais ensemble.
 */
export function exigeAccueil(
  membre: Pick<
    EtatDuMembre,
    "isSuperadmin" | "isFounder" | "joinedAtMs" | "profileCompletionRequiredAt" | "roleSlug"
  > & {
    /** `discordVerifiedAt`, en ms. `null` = pas encore vérifié. */
    discordVerifiedAtMs: number | null;
    /** `accueilTermineAt`, en ms. `null` = pas encore confirmé. */
    accueilTermineAtMs: number | null;
  },
  lancementMs: number = ACCUEIL_LAUNCH_MS,
): boolean {
  if (!estSoumisAuParcours(membre)) return false;
  if (membre.discordVerifiedAtMs === null) return false;
  if (membre.accueilTermineAtMs !== null) return false;
  return membre.discordVerifiedAtMs >= lancementMs;
}
